import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Search, Save, Loader2, Globe, Eye, BarChart3, Share2,
  Settings, Map, FileText, CheckCircle, AlertTriangle, XCircle,
  ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'general', label: 'Général', icon: Search },
  { id: 'sitemap', label: 'Sitemap', icon: Map },
  { id: 'pages', label: 'SEO par page', icon: FileText },
];

const PRIORITY_OPTIONS = ['0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'];
const FREQ_OPTIONS = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'];

// ============ SEO SCORE WIDGET ============
const SEOScore = ({ token }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/seo/score`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setData(r.data)).catch(() => {});
    }
  }, [token]);

  if (!data) return null;

  const { score, recommendations } = data;
  const color = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-yellow-400' : 'text-red-400';
  const bgColor = score >= 80 ? 'from-emerald-500/20' : score >= 50 ? 'from-yellow-500/20' : 'from-red-500/20';
  const ringColor = score >= 80 ? 'stroke-emerald-400' : score >= 50 ? 'stroke-yellow-400' : 'stroke-red-400';

  const okCount = recommendations.filter(r => r.status === 'ok').length;
  const missingCount = recommendations.filter(r => r.status === 'missing').length;
  const warningCount = recommendations.filter(r => r.status === 'warning').length;

  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className={`bg-gradient-to-r ${bgColor} to-slate-800/50 border border-blue-500/20 rounded-2xl p-6`} data-testid="seo-score-widget">
      <div className="flex items-center gap-6">
        {/* Circular progress */}
        <div className="relative flex-shrink-0">
          <svg width="100" height="100" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="6" className="text-slate-700" />
            <circle cx="50" cy="50" r="40" fill="none" strokeWidth="6" strokeLinecap="round"
              className={ringColor}
              strokeDasharray={circumference} strokeDashoffset={offset}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-2xl font-bold ${color}`} data-testid="seo-score-value">{score}%</span>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">Score SEO</h3>
          <div className="flex gap-4 text-sm">
            <span className="text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />{okCount} OK</span>
            <span className="text-yellow-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{warningCount} avertissement{warningCount > 1 ? 's' : ''}</span>
            <span className="text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />{missingCount} manquant{missingCount > 1 ? 's' : ''}</span>
          </div>
        </div>

        <button onClick={() => setExpanded(!expanded)} className="text-blue-400 hover:text-blue-300 transition-colors p-2" data-testid="seo-score-toggle">
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-blue-500/10 space-y-2" data-testid="seo-recommendations">
          {recommendations.map((r, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              {r.status === 'ok' && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
              {r.status === 'missing' && <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
              {r.status === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />}
              <span className={r.status === 'ok' ? 'text-blue-200/60' : r.status === 'warning' ? 'text-yellow-300' : 'text-red-300'}>
                {r.label}
              </span>
              {r.points > 0 && <span className="text-blue-300/30 text-xs ml-auto">{r.points} pts</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ============ SHARED FIELD COMPONENTS ============
const SeoInputField = ({ label, field, placeholder, type = 'text', hint, seo, update }) => (
  <div>
    <label className="block text-sm text-blue-300 mb-1.5">{label}</label>
    <input type={type} value={seo[field] || ''} onChange={e => update(field, e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      data-testid={`seo-${field}`} />
    {hint && <p className="text-xs text-blue-300/30 mt-1">{hint}</p>}
  </div>
);

const SeoTextAreaField = ({ label, field, placeholder, rows = 3, hint, maxLen, seo, update }) => (
  <div>
    <div className="flex justify-between items-center mb-1.5">
      <label className="text-sm text-blue-300">{label}</label>
      {maxLen && <span className={`text-xs ${(seo[field]?.length || 0) > maxLen ? 'text-red-400' : 'text-blue-300/30'}`}>{seo[field]?.length || 0}/{maxLen}</span>}
    </div>
    <textarea value={seo[field] || ''} onChange={e => update(field, e.target.value)}
      placeholder={placeholder} rows={rows}
      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
      data-testid={`seo-${field}`} />
    {hint && <p className="text-xs text-blue-300/30 mt-1">{hint}</p>}
  </div>
);


// ============ GENERAL TAB ============
const GeneralTab = ({ seo, update, saving, handleSave }) => {

  return (
    <div className="space-y-6">
      {/* Google Preview */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-blue-300/60 mb-3 flex items-center gap-2"><Eye className="w-4 h-4" /> Aperçu Google</h2>
        <div className="bg-white rounded-lg p-4">
          <p className="text-sm text-green-700 mb-0.5 truncate">{seo.canonical_url || 'https://www.sendbox.fr'} ›</p>
          <h3 className="text-lg text-blue-800 hover:underline cursor-pointer font-normal leading-tight mb-1 truncate">{seo.site_title || 'Titre du site'}</h3>
          <p className="text-sm text-gray-600 leading-snug line-clamp-2">{seo.meta_description || 'Description du site...'}</p>
        </div>
      </div>

      {/* Meta tags */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-500/20 rounded-lg"><Search className="w-5 h-5 text-blue-400" /></div>
          <h2 className="text-lg font-semibold text-white">Balises méta principales</h2>
        </div>
        <SeoInputField label="URL du site (production)" field="site_url" placeholder="https://sendbox.fr" hint="URL utilisée dans le sitemap et les liens SEO" seo={seo} update={update} />
        <SeoInputField label="Titre du site" field="site_title" placeholder="SENDBOX - Partage de fichiers sécurisé" hint="Idéal : 50-60 caractères" seo={seo} update={update} />
        <SeoTextAreaField label="Meta description" field="meta_description" placeholder="Description de votre site pour Google..." rows={2} maxLen={160} hint="Idéal : 150-160 caractères" seo={seo} update={update} />
        <SeoInputField label="Mots-clés" field="meta_keywords" placeholder="partage, fichiers, transfert, sécurisé" hint="Séparés par des virgules" seo={seo} update={update} />
        <SeoInputField label="URL canonique" field="canonical_url" placeholder="https://sendbox.com" hint="L'URL principale de votre site" seo={seo} update={update} />
      </div>

      {/* Open Graph */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-500/20 rounded-lg"><Share2 className="w-5 h-5 text-purple-400" /></div>
          <h2 className="text-lg font-semibold text-white">Réseaux sociaux (Open Graph)</h2>
        </div>
        <SeoInputField label="Titre OG" field="og_title" placeholder="SENDBOX - Partagez vos fichiers" hint="Titre affiché lors du partage sur les réseaux" seo={seo} update={update} />
        <SeoTextAreaField label="Description OG" field="og_description" placeholder="Description pour les réseaux sociaux..." rows={2} maxLen={200} seo={seo} update={update} />
        <SeoInputField label="Image OG (URL)" field="og_image" placeholder="https://sendbox.com/og-image.jpg" hint="1200x630px recommandé" seo={seo} update={update} />
        <div className="grid grid-cols-2 gap-4">
          <SeoInputField label="Twitter Card" field="twitter_card" placeholder="summary_large_image" seo={seo} update={update} />
          <SeoInputField label="Twitter @compte" field="twitter_site" placeholder="@sendbox" seo={seo} update={update} />
        </div>
      </div>

      {/* Indexation */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-emerald-500/20 rounded-lg"><Globe className="w-5 h-5 text-emerald-400" /></div>
          <h2 className="text-lg font-semibold text-white">Indexation & Robots</h2>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="seo-robots-index">
            <div className={`w-10 h-6 rounded-full transition-colors relative ${seo.robots_index ? 'bg-emerald-500' : 'bg-slate-600'}`}
              onClick={() => update('robots_index', !seo.robots_index)}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${seo.robots_index ? 'left-5' : 'left-1'}`} />
            </div>
            <span className="text-sm text-blue-200">Autoriser l'indexation (index)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="seo-robots-follow">
            <div className={`w-10 h-6 rounded-full transition-colors relative ${seo.robots_follow ? 'bg-emerald-500' : 'bg-slate-600'}`}
              onClick={() => update('robots_follow', !seo.robots_follow)}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${seo.robots_follow ? 'left-5' : 'left-1'}`} />
            </div>
            <span className="text-sm text-blue-200">Suivre les liens (follow)</span>
          </label>
        </div>
        <p className="text-xs text-blue-300/40">
          robots.txt : <code className="bg-slate-900/50 px-2 py-0.5 rounded text-blue-300/60">/api/robots.txt</code> — 
          sitemap : <code className="bg-slate-900/50 px-2 py-0.5 rounded text-blue-300/60">/api/sitemap.xml</code>
        </p>
      </div>

      {/* Analytics */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-500/20 rounded-lg"><BarChart3 className="w-5 h-5 text-blue-400" /></div>
          <h2 className="text-lg font-semibold text-white">Analytics & Tracking</h2>
        </div>
        <SeoInputField label="ID Google Analytics" field="google_analytics_id" placeholder="G-XXXXXXXXXX" hint="Tag de mesure Google Analytics 4" seo={seo} update={update} />
        <SeoInputField label="URL du favicon" field="favicon_url" placeholder="https://sendbox.com/favicon.ico" hint="Icône affichée dans l'onglet du navigateur" seo={seo} update={update} />
      </div>

      {/* Structured Data */}
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-cyan-500/20 rounded-lg"><Settings className="w-5 h-5 text-cyan-400" /></div>
          <h2 className="text-lg font-semibold text-white">Données structurées (Schema.org)</h2>
        </div>
        <SeoInputField label="Nom de l'application" field="structured_data_name" placeholder="SENDBOX" seo={seo} update={update} />
        <SeoTextAreaField label="Description Schema.org" field="structured_data_description" placeholder="Service de partage de fichiers sécurisé" rows={2} seo={seo} update={update} />
      </div>
    </div>
  );
};


// ============ SITEMAP TAB ============
const SitemapTab = ({ token }) => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pagesRes, seoRes] = await Promise.all([
          axios.get(`${API}/admin/seo/sitemap-pages`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/seo`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setPages(pagesRes.data.pages || []);
        setSiteUrl(seoRes.data.site_url || BACKEND_URL);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token]);

  const updatePage = (index, field, value) => {
    setPages(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/sitemap-pages`, { pages }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Sitemap sauvegardé' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>;

  const includedCount = pages.filter(p => p.included).length;

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Map className="w-5 h-5 text-blue-400" /> Gestion du Sitemap</h2>
            <p className="text-sm text-blue-300/50 mt-1">{includedCount} page{includedCount > 1 ? 's' : ''} incluse{includedCount > 1 ? 's' : ''} sur {pages.length}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href={`${siteUrl}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer"
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> Voir sitemap.xml
            </a>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-sitemap-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[1fr_120px_120px_80px] gap-3 text-xs text-blue-300/50 uppercase tracking-wide mb-3 px-2">
          <span>Page</span>
          <span>Priorité</span>
          <span>Fréquence</span>
          <span className="text-center">Incluse</span>
        </div>

        <div className="space-y-2">
          {pages.map((page, i) => (
            <div key={page.path} className={`grid grid-cols-[1fr_120px_120px_80px] gap-3 items-center px-3 py-3 rounded-xl border transition-colors ${page.included ? 'bg-slate-900/30 border-blue-500/10' : 'bg-slate-900/10 border-slate-700/30 opacity-50'}`}
              data-testid={`sitemap-row-${i}`}>
              <div>
                <span className="text-sm text-white font-medium">{page.label}</span>
                <span className="text-xs text-blue-300/30 ml-2">{page.path}</span>
                {page.is_cms && <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded ml-2">CMS</span>}
              </div>
              <select value={page.priority} onChange={e => updatePage(i, 'priority', e.target.value)}
                className="bg-slate-900/50 border border-blue-500/20 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                data-testid={`sitemap-priority-${i}`}>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={page.changefreq} onChange={e => updatePage(i, 'changefreq', e.target.value)}
                className="bg-slate-900/50 border border-blue-500/20 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                data-testid={`sitemap-freq-${i}`}>
                {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <div className="flex justify-center">
                <div className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${page.included ? 'bg-emerald-500' : 'bg-slate-600'}`}
                  onClick={() => updatePage(i, 'included', !page.included)} data-testid={`sitemap-toggle-${i}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${page.included ? 'left-5' : 'left-1'}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============ PAGE SEO TAB ============
const PageSEOTab = ({ token }) => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const loadPages = useCallback(() => {
    axios.get(`${API}/admin/seo/page-seo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setPages(r.data.pages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadPages(); }, [loadPages]);

  const startEdit = (page) => {
    setEditingPage(page.path);
    setEditData({
      seo_title: page.seo_title || '',
      seo_description: page.seo_description || '',
      seo_keywords: page.seo_keywords || '',
      seo_og_image: page.seo_og_image || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/page-seo`, { page_path: editingPage, ...editData }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `SEO sauvegardé pour ${editingPage}` });
      setEditingPage(null);
      loadPages();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>;

  const configuredCount = pages.filter(p => p.has_custom_seo).length;

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> SEO par page</h2>
          <p className="text-sm text-blue-300/50 mt-1">{configuredCount} page{configuredCount > 1 ? 's' : ''} configurée{configuredCount > 1 ? 's' : ''} sur {pages.length}</p>
        </div>

        <div className="space-y-2">
          {pages.map((page) => (
            <div key={page.path}>
              <div className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-colors cursor-pointer hover:bg-slate-800/70 ${editingPage === page.path ? 'bg-slate-800/70 border-blue-500/30' : 'bg-slate-900/30 border-blue-500/10'}`}
                onClick={() => editingPage === page.path ? setEditingPage(null) : startEdit(page)}
                data-testid={`page-seo-row-${page.path}`}>
                <div className="flex items-center gap-3">
                  {page.has_custom_seo ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <div className="w-4 h-4 rounded-full border border-blue-500/30" />}
                  <div>
                    <span className="text-sm text-white font-medium">{page.label}</span>
                    <span className="text-xs text-blue-300/30 ml-2">{page.path}</span>
                    {page.is_cms && <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded ml-2">CMS</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {page.has_custom_seo && <span className="text-xs text-emerald-400/70 bg-emerald-500/10 px-2 py-0.5 rounded-full">Personnalisé</span>}
                  {editingPage === page.path ? <ChevronUp className="w-4 h-4 text-blue-400" /> : <ChevronDown className="w-4 h-4 text-blue-400" />}
                </div>
              </div>

              {editingPage === page.path && (
                <div className="mt-2 bg-slate-900/40 border border-blue-500/10 rounded-xl p-5 space-y-4" data-testid="page-seo-edit-form">
                  {/* Google Preview for this page */}
                  <div className="bg-white rounded-lg p-3 mb-4">
                    <p className="text-xs text-green-700 mb-0.5 truncate">https://www.sendbox.fr{page.path} ›</p>
                    <h4 className="text-base text-blue-800 font-normal leading-tight mb-0.5 truncate">
                      {editData.seo_title || page.label}
                    </h4>
                    <p className="text-xs text-gray-600 leading-snug line-clamp-2">
                      {editData.seo_description || 'Aucune description définie...'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-blue-300 mb-1.5">Titre SEO</label>
                    <input type="text" value={editData.seo_title} onChange={e => setEditData(prev => ({ ...prev, seo_title: e.target.value }))}
                      placeholder={page.label}
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                      data-testid="page-seo-title" />
                    <p className="text-xs text-blue-300/30 mt-1">{editData.seo_title?.length || 0}/60 caractères</p>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-300 mb-1.5">Meta description</label>
                    <textarea value={editData.seo_description} onChange={e => setEditData(prev => ({ ...prev, seo_description: e.target.value }))}
                      placeholder="Description spécifique à cette page..."
                      rows={2}
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none"
                      data-testid="page-seo-description" />
                    <p className={`text-xs mt-1 ${(editData.seo_description?.length || 0) > 160 ? 'text-red-400' : 'text-blue-300/30'}`}>{editData.seo_description?.length || 0}/160 caractères</p>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-300 mb-1.5">Mots-clés</label>
                    <input type="text" value={editData.seo_keywords} onChange={e => setEditData(prev => ({ ...prev, seo_keywords: e.target.value }))}
                      placeholder="mot-clé1, mot-clé2, ..."
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                      data-testid="page-seo-keywords" />
                  </div>
                  <div>
                    <label className="block text-sm text-blue-300 mb-1.5">Image OG (URL)</label>
                    <input type="text" value={editData.seo_og_image} onChange={e => setEditData(prev => ({ ...prev, seo_og_image: e.target.value }))}
                      placeholder="https://..."
                      className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                      data-testid="page-seo-og-image" />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="ghost" onClick={() => setEditingPage(null)} className="text-blue-300 hover:text-white">Annuler</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-page-seo-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Sauvegarder
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============ MAIN COMPONENT ============
const AdminSEO = () => {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [seo, setSeo] = useState({
    site_url: '', site_title: '', meta_description: '', meta_keywords: '',
    og_title: '', og_description: '', og_image: '', favicon_url: '',
    google_analytics_id: '', robots_index: true, robots_follow: true,
    canonical_url: '', twitter_card: 'summary_large_image', twitter_site: '',
    structured_data_name: '', structured_data_description: ''
  });

  useEffect(() => {
    if (token) loadSEO();
  }, [token]);

  const loadSEO = async () => {
    try {
      const res = await axios.get(`${API}/admin/seo`, { headers: { Authorization: `Bearer ${token}` } });
      setSeo(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      console.error('Error loading SEO:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { type, ...data } = seo;
      await axios.put(`${API}/admin/seo`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Paramètres SEO sauvegardés' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setSeo(prev => ({ ...prev, [field]: value }));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <Toaster />
      <NavigationMenu />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Search className="w-6 h-6 text-blue-400" />
                <h1 className="text-xl font-semibold text-white" data-testid="seo-page-title">SEO & Référencement</h1>
              </div>
            </div>
            {activeTab === 'general' && (
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-seo-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Sauvegarder
              </Button>
            )}
          </div>

          {/* SEO Score */}
          <div className="mb-6">
            <SEOScore token={token} />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-slate-800/50 border border-blue-500/20 rounded-xl p-1 mb-6" data-testid="seo-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg' : 'text-blue-300/60 hover:text-blue-200 hover:bg-slate-700/50'}`}
                  data-testid={`seo-tab-${tab.id}`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === 'general' && <GeneralTab seo={seo} update={update} saving={saving} handleSave={handleSave} />}
          {activeTab === 'sitemap' && <SitemapTab token={token} />}
          {activeTab === 'pages' && <PageSEOTab token={token} />}
        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminSEO;
